/**
 * Conversa dentro da solicitação (PM-12): dúvidas entre PCP, gestão e obra
 * ficam registradas ali, com menção. Quem é marcado recebe pendência no sino
 * e em Minhas Pendências até abrir; abrir a conversa marca como lida.
 * Comentário não se edita — é registro. Usado no detalhe da Gestão e no do
 * colaborador.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAtSign, FiX } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import useCliqueFora from "../../../hooks/useCliqueFora";
import {
  comentarSolicitacao, listarComentariosSolicitacao, pessoasMencionaveis,
} from "../../../services/solicitacoes";

const MAX = 4000;

const caixa = {
  border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface)", padding: "10px 12px",
};
const chip = {
  display: "inline-flex", alignItems: "center", gap: 4, height: 22, padding: "0 8px", borderRadius: 999,
  fontSize: 11.5, fontWeight: 600, background: "var(--surface-2)", color: "var(--ink-2)",
};

function dataHora(iso) {
  return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** Quem foi marcado e ainda não abriu — o "aguardando resposta". */
export function aguardando(comentarios) {
  const nomes = new Set();
  (comentarios || []).forEach((c) => (c.mencoes || []).forEach((m) => { if (!m.lida) nomes.add(m.nome); }));
  return [...nomes];
}

export function SeletorPessoas({ pessoas, marcadas, onAlternar }) {
  const [aberto, setAberto] = useState(false);
  const fora = useCliqueFora(() => setAberto(false), { ativo: aberto });
  const [busca, setBusca] = useState("");
  const q = busca.trim().toLowerCase();
  const visiveis = pessoas.filter((p) => p.nome.toLowerCase().includes(q));
  return (
    <div style={{ position: "relative" }} {...fora}>
      <button type="button" className="btn btn--ghost" onClick={() => setAberto((v) => !v)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }} aria-expanded={aberto}>
        <FiAtSign size={13} /> Marcar pessoa
      </button>
      {aberto && (
        <div style={{ ...caixa, position: "absolute", bottom: "calc(100% + 6px)", left: 0, zIndex: 30, width: 260,
                      maxHeight: 260, overflowY: "auto", boxShadow: "var(--shadow-2, 0 12px 32px rgba(0,0,0,.28))" }}>
          <input autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar pessoa"
                 aria-label="Buscar pessoa para marcar"
                 style={{ width: "100%", height: 32, marginBottom: 6, padding: "0 8px", borderRadius: 8,
                          border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--ink)" }} />
          {visiveis.length === 0 && <div style={{ fontSize: 12.5, color: "var(--ink-3)", padding: 6 }}>Ninguém com esse nome.</div>}
          {visiveis.map((p) => (
            <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 2px", fontSize: 13, cursor: "pointer", color: "var(--ink)" }}>
              <input type="checkbox" checked={marcadas.has(p.id)} onChange={() => onAlternar(p.id)} />
              {p.nome}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ConversaSolicitacao({ familia, id, titulo = "Conversa" }) {
  const { request } = useAuth();
  const [comentarios, setComentarios] = useState(null);
  const [pessoas, setPessoas] = useState([]);
  const [texto, setTexto] = useState("");
  const [marcadas, setMarcadas] = useState(() => new Set());
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    try {
      const r = await listarComentariosSolicitacao(request, familia, id);
      setComentarios(r?.comentarios || []);
    } catch (e) {
      setErro(e?.message || "Não foi possível carregar a conversa.");
    }
  }, [request, familia, id]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    pessoasMencionaveis(request, familia, id).then((r) => setPessoas(r || []))
      // Sem a lista, comenta-se sem marcar ninguém.
      .catch(() => setPessoas([]));
  }, [request, familia, id]);

  const porId = useMemo(() => Object.fromEntries(pessoas.map((p) => [p.id, p])), [pessoas]);
  const esperando = aguardando(comentarios);

  const alternar = (pid) => setMarcadas((s) => {
    const prox = new Set(s);
    if (prox.has(pid)) prox.delete(pid); else prox.add(pid);
    return prox;
  });

  const enviar = async () => {
    if (!texto.trim()) { setErro("Escreva o comentário."); throw new Error("vazio"); }
    const r = await comentarSolicitacao(request, familia, id, texto.trim(), [...marcadas]);
    setComentarios(r?.comentarios || []);
    setTexto("");
    setMarcadas(new Set());
    setErro("");
  };

  return (
    <section style={{ marginTop: 18 }} aria-label={titulo}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <h4 style={{ margin: 0, fontSize: 13, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--ink-3)" }}>{titulo}</h4>
        {esperando.length > 0 && (
          <span style={{ ...chip, background: "var(--warning-bg)", color: "var(--warning-text)" }}
                title="Pessoas marcadas que ainda não abriram a conversa">
            Aguardando resposta de {esperando.join(", ")}
          </span>
        )}
      </div>

      {comentarios === null && !erro && <div style={{ fontSize: 13, color: "var(--ink-3)" }}>Carregando…</div>}
      {comentarios?.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--ink-3)", marginBottom: 8 }}>
          Nenhum comentário ainda. Use para tirar dúvidas com quem pediu ou com a gestão.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(comentarios || []).map((c) => (
          <div key={c.id} style={caixa}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12.5, color: "var(--ink-3)" }}>
              <strong style={{ color: "var(--ink)" }}>{c.autor_nome}</strong>
              <span>{dataHora(c.criado_em)}</span>
            </div>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 13.5, color: "var(--ink)", marginTop: 4 }}>{c.texto}</div>
            {(c.mencoes || []).length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                {c.mencoes.map((m) => (
                  <span key={m.usuario_id} style={chip} title={m.lida ? "Já viu" : "Ainda não viu"}>
                    @{m.nome}{m.lida ? " ✓" : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ ...caixa, marginTop: 10 }}>
        <textarea
          value={texto}
          maxLength={MAX}
          onChange={(e) => { setTexto(e.target.value); setErro(""); }}
          placeholder="Escreva um comentário…"
          aria-label="Novo comentário"
          style={{ width: "100%", minHeight: 64, border: 0, outline: "none", resize: "vertical",
                   background: "transparent", color: "var(--ink)", fontSize: 13.5 }}
        />
        {marcadas.size > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
            {[...marcadas].map((pid) => (
              <span key={pid} style={chip}>
                @{porId[pid]?.nome || "pessoa"}
                <button type="button" aria-label={`Desmarcar ${porId[pid]?.nome || "pessoa"}`} onClick={() => alternar(pid)}
                        style={{ border: 0, background: "none", color: "inherit", cursor: "pointer", padding: 0, display: "inline-flex" }}>
                  <FiX size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          {pessoas.length > 0 ? <SeletorPessoas pessoas={pessoas} marcadas={marcadas} onAlternar={alternar} /> : <span />}
          <AsyncButton variant="primary" successMessage="Comentário enviado." onClick={enviar}>
            Comentar
          </AsyncButton>
        </div>
        {erro && <div style={{ fontSize: 12.5, color: "var(--danger-text)", marginTop: 4 }}>{erro}</div>}
      </div>
    </section>
  );
}
